import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { MessageSquareX, RefreshCw, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  onReset?: () => void;
  onNewChat?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Chat-specific Error Boundary
 * 
 * Handles errors in chat interface, message sending, and AI responses.
 * Provides recovery options without losing conversation history.
 */
export class ChatErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to Sentry with chat-specific context
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', 'chat');
      scope.setContext('chatError', {
        componentStack: errorInfo.componentStack,
        errorMessage: error.message,
      });
      Sentry.captureException(error);
    });

    if (import.meta.env.DEV) {
      console.error('ChatErrorBoundary caught error:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
    this.props.onReset?.();
  };

  handleNewChat = () => {
    this.setState({
      hasError: false,
      error: null,
    });
    this.props.onNewChat?.();
  };

  render() {
    if (this.state.hasError) {
      const isAIError = this.state.error?.message.includes('AI') || 
                        this.state.error?.message.includes('response') ||
                        this.state.error?.message.includes('generate');

      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <MessageSquareX className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {isAIError ? 'Chat Service Error' : 'Chat Error'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isAIError 
                    ? 'Unable to connect to AI assistant'
                    : 'Something went wrong with the chat'}
                </p>
              </div>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className="mb-4 p-3 bg-muted rounded-md">
                <p className="text-xs font-mono text-destructive">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <div className="mb-6 space-y-2">
              <p className="text-sm text-muted-foreground">
                Possible causes:
              </p>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                <li>Network connection issues</li>
                {isAIError && <li>AI service temporarily unavailable</li>}
                <li>Session expired or invalid</li>
                <li>Message processing error</li>
              </ul>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              Your conversation history is safe. You can try again or start a new chat.
            </p>

            <div className="flex gap-3">
              <Button
                onClick={this.handleReset}
                variant="default"
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Chat
              </Button>
              {this.props.onNewChat && (
                <Button
                  onClick={this.handleNewChat}
                  variant="outline"
                  className="flex-1"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Chat
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
